interface CacheEntry<T> {
	data: T
	timestamp: number
	expiresAt: number
}

interface RTQCacheParams {
	[key: string]: any
}

class RTKQueryCache {
	private cache = new Map<string, CacheEntry<any>>()
	private readonly CACHE_DURATION = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

	private generateCacheKey(endpoint: string, params: RTQCacheParams): string {
		// Normalize params to ensure consistent cache keys
		const normalizedParams = this.normalizeParams(params)
		const sortedParams = Object.keys(normalizedParams)
			.sort()
			.reduce(
				(result, key) => {
					result[key] = normalizedParams[key]
					return result
				},
				{} as Record<string, any>,
			)

		return `${endpoint}:${JSON.stringify(sortedParams)}`
	}

	private normalizeParams(params: RTQCacheParams): RTQCacheParams {
		const normalized: RTQCacheParams = {}

		Object.entries(params).forEach(([key, value]) => {
			// Skip undefined, null, empty strings
			if (value !== undefined && value !== null && value !== '') {
				// Convert arrays to sorted strings for consistent caching
				if (Array.isArray(value)) {
					normalized[key] = [...value].sort().join(',')
				} else {
					normalized[key] = value
				}
			}
		})

		return normalized
	}

	private isExpired(entry: CacheEntry<any>): boolean {
		return Date.now() > entry.expiresAt
	}

	get<T>(endpoint: string, params: RTQCacheParams): T | null {
		const key = this.generateCacheKey(endpoint, params)
		const entry = this.cache.get(key)

		if (!entry || this.isExpired(entry)) {
			if (entry) {
				this.cache.delete(key)
			}
			return null
		}

		return entry.data
	}

	set<T>(endpoint: string, params: RTQCacheParams, data: T): void {
		const key = this.generateCacheKey(endpoint, params)
		const now = Date.now()

		this.cache.set(key, {
			data,
			timestamp: now,
			expiresAt: now + this.CACHE_DURATION,
		})
	}

	clear(): void {
		this.cache.clear()
	}

	cleanExpired(): void {
		this.cache.forEach((entry, key) => {
			if (this.isExpired(entry)) {
				this.cache.delete(key)
			}
		})
	}

	getCacheStats(): { totalEntries: number; expiredEntries: number } {
		let expired = 0
		this.cache.forEach(entry => {
			if (this.isExpired(entry)) expired++
		})

		return {
			totalEntries: this.cache.size,
			expiredEntries: expired,
		}
	}
}

export const rtkQueryCache = new RTKQueryCache()

// Clean expired entries every 30 minutes
setInterval(
	() => {
		rtkQueryCache.cleanExpired()
	},
	30 * 60 * 1000,
)
